import React, { useEffect, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import toast from "react-hot-toast";
import { Plus, Edit2, Truck } from "react-feather";
import SectionCard from "../../../components/common/SectionCard";
import { useBranchFilter } from "../../branchFilter/useBranchFilter";
import { useHasPermission } from "../../auth/useClaims";
import { useGetVehiclesQuery } from "../../masters/vehicles/vehiclesApi";
import {
  useCreateMobileUnitMutation,
  useGetMobileUnitDeviceUsersQuery,
  useGetMobileUnitsQuery,
  useUpdateMobileUnitMutation,
} from "../alarmsApi";
import { ALARM_STATUS_LABEL } from "../alarmFormat";

// A mobile unit is a patrol car. Its DEVICE LOGIN is the account the car's
// phone/tablet signs in with - alarms dispatched to the unit are pushed to that
// login, and only that login can open and report on them. Logins listed here
// are the ones holding Alarms.Respond (the MOBILE_UNIT role); create the login
// under Users first.
const UnitModal = ({ unit, show, onClose, branches, defaultBranchId }) => {
  const isEdit = !!unit;
  const [branchId, setBranchId] = useState("");
  const [unitName, setUnitName] = useState("");
  const [vehicleId, setVehicleId] = useState("");
  const [deviceUserId, setDeviceUserId] = useState("");
  const [remarks, setRemarks] = useState("");
  const [isActive, setIsActive] = useState(true);

  const canViewVehicles = useHasPermission("Vehicles.View");
  const canEditVehicles = useHasPermission("Vehicles.Edit");
  const canSeeVehicles = canViewVehicles || canEditVehicles;
  const { data: deviceUsers = [] } = useGetMobileUnitDeviceUsersQuery(undefined, { skip: !show });
  const { data: vehicles = [] } = useGetVehiclesQuery({}, { skip: !show || !canSeeVehicles });
  const [createUnit, createState] = useCreateMobileUnitMutation();
  const [updateUnit, updateState] = useUpdateMobileUnitMutation();
  const saving = createState.isLoading || updateState.isLoading;

  useEffect(() => {
    if (!show) return;
    setBranchId(String(unit?.branchId ?? defaultBranchId ?? branches[0]?.branchId ?? ""));
    setUnitName(unit?.unitName || "");
    setVehicleId(unit?.vehicleId ? String(unit.vehicleId) : "");
    setDeviceUserId(unit?.deviceUserId ? String(unit.deviceUserId) : "");
    setRemarks(unit?.remarks || "");
    setIsActive(unit?.isActive ?? true);
  }, [show, unit, defaultBranchId, branches]);

  const branchVehicles = vehicles.filter((v) => String(v.branchId) === branchId);

  const save = async () => {
    const body = {
      unitName: unitName.trim(),
      vehicleId: vehicleId ? Number(vehicleId) : null,
      deviceUserId: deviceUserId ? Number(deviceUserId) : null,
      remarks: remarks.trim() || null,
    };
    try {
      if (isEdit) await updateUnit({ recordGuid: unit.recordGuid, ...body, isActive }).unwrap();
      else await createUnit({ branchId: Number(branchId), ...body }).unwrap();
      toast.success(isEdit ? "Mobile unit updated" : "Mobile unit created");
      onClose();
    } catch (e) {
      toast.error(e?.message || "Could not save the unit");
    }
  };

  return (
    <Modal show={show} onHide={saving ? undefined : onClose} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>{isEdit ? `Edit ${unit.unitName}` : "New mobile unit"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <label className="form-label">Branch <span className="text-danger">*</span></label>
          <select className="form-select" value={branchId} disabled={isEdit} onChange={(e) => { setBranchId(e.target.value); setVehicleId(""); }}>
            {branches.map((b) => <option key={b.branchId} value={b.branchId}>{b.branchCode}</option>)}
          </select>
        </div>
        <div className="mb-3">
          <label className="form-label">Unit name <span className="text-danger">*</span></label>
          <input className="form-control" maxLength={60} value={unitName} placeholder="e.g. Patrol 1 - Nyali" onChange={(e) => setUnitName(e.target.value)} />
        </div>
        <div className="mb-3">
          <label className="form-label">Device login</label>
          <select className="form-select" value={deviceUserId} onChange={(e) => setDeviceUserId(e.target.value)}>
            <option value="">None yet</option>
            {deviceUsers.map((u) => (
              <option
                key={u.userId}
                value={u.userId}
                disabled={!!u.linkedUnitName && String(u.userId) !== String(unit?.deviceUserId ?? "")}
              >
                {u.fullName} ({u.username}){u.linkedUnitName && String(u.userId) !== String(unit?.deviceUserId ?? "") ? ` - used by ${u.linkedUnitName}` : ""}
              </option>
            ))}
          </select>
          <small className="text-muted">
            Only logins with the Mobile Unit role are listed. Without one, alarms cannot be sent to this unit.
          </small>
        </div>
        {canSeeVehicles && (
          <div className="mb-3">
            <label className="form-label">Vehicle</label>
            <select className="form-select" value={vehicleId} onChange={(e) => setVehicleId(e.target.value)}>
              <option value="">Not linked</option>
              {branchVehicles.map((v) => <option key={v.vehicleId} value={v.vehicleId}>{v.regNo} - {v.brand} {v.model}</option>)}
            </select>
          </div>
        )}
        <div className="mb-3">
          <label className="form-label">Remarks</label>
          <input className="form-control" maxLength={200} value={remarks} onChange={(e) => setRemarks(e.target.value)} />
        </div>
        {isEdit && (
          <div className="form-check form-switch">
            <input className="form-check-input" type="checkbox" id="unit-active" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
            <label className="form-check-label" htmlFor="unit-active">Active</label>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="primary" onClick={save} disabled={saving || !unitName.trim() || !branchId}>
          {saving ? "Saving..." : "Save"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const MobileUnitsPage = () => {
  const { selectedBranchId, availableBranches } = useBranchFilter();
  const [includeInactive, setIncludeInactive] = useState(false);
  const [editing, setEditing] = useState(undefined); // undefined = closed, null = new, unit = edit
  const { data: units = [], isLoading } = useGetMobileUnitsQuery({
    ...(selectedBranchId != null ? { branchId: selectedBranchId } : {}),
    includeInactive,
  });

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Mobile Units</h4>
              <h6>Rapid response units the control room dispatches alarms to, and the device login each one uses</h6>
            </div>
          </div>
          <div className="page-btn">
            <button type="button" className="btn btn-primary d-inline-flex align-items-center gap-1" onClick={() => setEditing(null)}>
              <Plus size={15} /> New unit
            </button>
          </div>
        </div>

        <SectionCard
          icon={Truck}
          title={`${units.length} unit${units.length === 1 ? "" : "s"}`}
          right={
            <div className="form-check form-switch mb-0">
              <input className="form-check-input" type="checkbox" id="show-inactive" checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)} />
              <label className="form-check-label small" htmlFor="show-inactive">Show inactive</label>
            </div>
          }
        >
          {isLoading ? (
            <p className="text-muted small mb-0">Loading...</p>
          ) : units.length === 0 ? (
            <p className="text-muted small mb-0">No mobile units yet.</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm align-middle mb-0">
                <thead>
                  <tr><th>Unit</th><th>Branch</th><th>Vehicle</th><th>Device login</th><th>Now</th><th /></tr>
                </thead>
                <tbody>
                  {units.map((u) => (
                    <tr key={u.recordGuid} className={u.isActive ? "" : "text-muted"}>
                      <td className="fw-bold">{u.unitName}{!u.isActive && <span className="badge badge-linesecondary ms-2">Inactive</span>}
                        {u.remarks && <div className="small text-muted fw-normal">{u.remarks}</div>}
                      </td>
                      <td>{u.branchCode}</td>
                      <td>{u.vehicleRegNo || "—"}</td>
                      <td>{u.deviceUserFullName ? `${u.deviceUserFullName} (${u.deviceUsername})` : <span className="text-danger">None - cannot receive alarms</span>}</td>
                      <td>{u.activeResponseNumber ? `${u.activeResponseNumber} · ${ALARM_STATUS_LABEL[u.activeResponseStatus]}` : u.isActive ? "Free" : "—"}</td>
                      <td className="text-end">
                        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setEditing(u)} title="Edit">
                          <Edit2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>

        <UnitModal
          show={editing !== undefined}
          unit={editing || null}
          onClose={() => setEditing(undefined)}
          branches={availableBranches}
          defaultBranchId={selectedBranchId}
        />
      </div>
    </div>
  );
};

export default MobileUnitsPage;
